import {
  type HTMLAttributes,
  type RefCallback,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {type BoardCardRoom, draggingCardRoom, settlingCardRoom} from '../drag/card-room'
import {useDroppable} from '../drag/dnd-kit'
import {
  type BoardDropPlacement,
  cellDropAnchor,
  type DrawnCard,
  insertionIndexAt,
  isRedundantInsertion,
} from '../drag/placement'
import {
  BOARD_CARD_ID_ATTRIBUTE,
  BOARD_CELL_DROP_TYPE,
  type BoardCellDropData,
  boardCellDropId,
  readBoardCellDropData,
} from '../drag/types'
import {cellPosition} from '../focus/positions'
import type {BoardFocusPosition} from '../focus/types'
import {useFocusedCardIndex, useIsCardFocusPending} from '../focus/use-focused-card-index'
import {useRovingFocus} from '../focus/use-roving-focus'
import {useBoardItemApproach} from '../paging/use-board-item-approach'
import {composeCellKey} from '../provider/cell-key'
import {
  type BoardHeadingElementLevel,
  useBoardCanMoveCards,
  useBoardCellHeadingLevel,
  useBoardCellOrdering,
  useBoardConfig,
  useBoardDataContext,
  useBoardDragPointerYRef,
  useBoardDropPlacementRef,
  useBoardInteraction,
  useBoardSettlingMove,
} from '../provider/contexts'
import {useResetScroll} from '../scroll/reset-scroll'
import {VERTICAL_DEAD_ZONE_RATIO} from '../scroll/settings'
import {useAutoScroll} from '../scroll/use-auto-scroll'
import {useScrollEdges} from '../scroll/use-scroll-edges'
import type {CellAddress, ItemId} from '../types'
import {useElementRef} from '../use-element-ref'
import {cardSetSize} from './card-set-size'
import {cellHasUnloadedItems} from './cell-has-unloaded-items'

export type UseBoardCellResult = {
  /**
   * The ids of the cards loaded in this cell, in the order they should appear.
   *
   * Ids rather than items, so this hook is not generic over the caller's item type. A
   * component doing layout, drag or keyboard work needs to know which cards are here
   * and in what order, not what they contain. The one caller that draws card content
   * reads the items from `useBoardData`, which is the generic hook.
   *
   * These line up index for index with the cell's `items` in `useBoardData`, because
   * both come from the same array in the same order.
   */
  itemIds: readonly ItemId[]
  /** The server's count for this cell. Never `itemIds.length`. */
  totalCount?: number
  /** Hidden column heading for this intersection, absent for an invalid address. */
  heading: string | undefined
  headingLevel: BoardHeadingElementLevel
  /**
   * How many cards are in this cell, for whatever describes it, such as a selection summary.
   *
   * Every card the cell has, not every card it has loaded. Those are different numbers on
   * a board that pages, and the loaded one changes under a reader every time another page
   * arrives.
   *
   * So the count the caller stated wins, floored at what is loaded, because a count from a
   * server can be stale and a count smaller than the cards already present is not true.
   * `-1` means the total is not known: the cell says it has more and never said how many.
   *
   * Answered here rather than by whatever describes the cell, so descriptions of the same
   * cell cannot drift apart.
   */
  cardSetSize: number
  /** This cell has a further page. */
  hasMoreItems: boolean
  /** A page for this cell is in flight. */
  isLoadingItems: boolean
  cellProps: HTMLAttributes<HTMLElement> & {ref?: RefCallback<HTMLElement>}
  /**
   * A drag is over this cell, which makes it the cell a drop would land in.
   *
   * The drag library's answer rather than a pointer test of this hook's own: the cell it
   * names is whichever one the collision strategy chose, and `drag/BoardDragProvider.tsx`
   * records which strategy that is and why. Under the one set there it is the cell the
   * pointer is inside, and no cell at all while the pointer is between two of them.
   *
   * **Only for a card of this board's.** A board sits inside a caller's own tree and
   * anything in there may be draggable, and the drag library does not care what is being
   * carried, so without this a caller dragging anything at all across the board would
   * light its cells up. The same fact the auto-scroll already gates on, and the same rule
   * `onDragChange` states: a drag this package cannot read is somebody else's.
   *
   * `false` when nothing is being dragged, and `false` for a key with no column or no row
   * behind it, which is not a cell on this board and answers no drop. `false` on every cell
   * of a board whose caller supplied no `onItemsMove`, which registers no drop target and
   * no drag source at all: a cell that lit up for a drop nobody would be told about would
   * be marking a destination the board cannot deliver.
   */
  isOver: boolean
  /**
   * Whether the caller ranks this cell by hand, through `BoardProvider`'s `canReorderCards`,
   * which is what makes `dropIndex` answer here. `false` for a key with no column or no row
   * behind it, and on every cell of a board that moves no cards.
   */
  canReorderCards: boolean
  /**
   * Where among this cell's loaded cards the held card would land, or `null` when nothing
   * is being aimed at this cell.
   *
   * An index into {@link UseBoardCellResult.itemIds}, so `0` is above the first loaded card
   * and `itemIds.length` is below the last. It is what draws the place a card would go, and
   * it is answered only on a cell the caller hand-orders through `BoardProvider`'s
   * `canReorderCards`: a cell whose order is the board's sort has no position to aim at, so
   * it offers none.
   *
   * **Drawn even when dropping there would change nothing**, which is the drop back into
   * the place a card already occupies. Going quiet at the two positions either side of the
   * held card would leave a dead patch where the board stopped answering somebody who had
   * dragged nowhere, which reads as a fault rather than as a rule. Whether such a drop is
   * reported is a separate question, and `drag/resolve-drop.ts` answers it.
   *
   * Measured against the cards that are drawn rather than the cards the cell has loaded.
   * A cell windows its cards, so the drawn run is the part a reader can see, and the ends
   * of it are the ends of what they are aiming at. The held card is left out in the cell it
   * came from, because its slot is where the room for it opens rather than a card to aim at.
   */
  dropIndex: number | null
  /**
   * The space this cell keeps for the card being moved, or `null` when no card is being
   * moved.
   *
   * Present in every cell from the start of a drag, so a styled layer can animate a room
   * closing as well as opening. After a drop it stays in the cells the move touches until
   * the caller's data shows the move, or for a tenth of a second when it does not, and it
   * goes back to `null` in the same render that shows the card where it landed.
   *
   * `@github-ui/board` draws it as a placeholder the size of the held card, and moves the
   * cards around it to make room.
   */
  cardRoom: BoardCardRoom | null
  /**
   * The part that holds the cards, and the cell's own vertical scroll container.
   *
   * It is also the cell's `role="group"`, named by its column and, on a swimlaned board,
   * its swimlane.
   *
   * A cell scrolls independently of its neighbours, because a person can be at the
   * bottom of one column and the top of the next. That is also what gives the cell's
   * card window something to measure against when virtualization lands.
   *
   * `tabIndex` is this element's share of the board's roving tabindex: `0` when the focus
   * position names this cell rather than a card in it, `-1` otherwise. A cell holding
   * cards is reached through them, so its body is the roving item only when it is empty.
   *
   * The element is also marked with `data-scroll-block-start` and `data-scroll-block-end`
   * whenever it still has cards that way, written straight to it rather than returned.
   * See `useScrollEdges`.
   */
  bodyProps: HTMLAttributes<HTMLElement> & {ref: RefCallback<HTMLElement>}
  /**
   * The element {@link UseBoardCellResult.bodyProps} was spread onto, which is this
   * cell's own scroll container.
   *
   * Returned as well as spread, for the same reason `useBoardRoot` returns the board's:
   * a window over this cell's cards has to measure against it and move it without owning
   * it.
   */
  bodyRef: RefObject<HTMLElement | null>
  /**
   * Where the board's focus position sits in `itemIds`, or `null` when the position is
   * not on a card in this cell.
   *
   * A cell that windows its cards must render this one whatever its scroll offset says,
   * because a position is not somewhere focus is until an element at it exists. It is
   * also the index a window moves to when focus is owed to a card nobody has scrolled to
   * yet. See `useFocusedCardIndex` for why both are the same number.
   */
  focusedItemIndex: number | null
  /**
   * Focus has been asked for on a card in this cell and nothing has taken it yet.
   *
   * What tells this cell's card window that it may move. Landing focus already scrolls a
   * cell in the ordinary case, because focusing an element reveals it; a windowed cell is
   * the case where that is not enough, since the card is focused before the window has
   * placed it and the offset the browser chose describes a layout that is about to be
   * replaced. See `useIsCardFocusPending` for why the index alone is not the signal.
   */
  isCardFocusPending: boolean
  /**
   * Whether to draw an empty state. A question about what to render, not a count: the
   * cell's count is `totalCount`, which the server states and which may be larger than
   * what is loaded.
   */
  isEmpty: boolean
}

/**
 * One cell: the intersection of a column and a row, where cards live and what a drop
 * resolves to. Every column-and-row pair has one, whether or not the caller supplied it,
 * and every one of them is a drop target on a board that moves cards.
 *
 * A pair the caller did not supply is answered here as an empty cell, with no items, no
 * count and nothing more to page. An absent cell and a supplied empty one produce the
 * same result, so nothing downstream has to know which it was looking at.
 */
export function useBoardCell(address: CellAddress): UseBoardCellResult {
  const {columnsByKey, rowsByKey, cellsByAddress} = useBoardDataContext()
  const {getItemId, labels, hasRowHeaders, resetScrollKey, cellAutoScrollRate} = useBoardConfig()
  const {draggingCards} = useBoardInteraction()
  const cellHeadingLevel = useBoardCellHeadingLevel()
  const {columnKey, rowKey} = address
  const {ref: bodyRef, setRef} = useElementRef()
  const isDragging = draggingCards.length > 0

  useAutoScroll({
    active: isDragging,
    scrollRef: bodyRef,
    // Locked downwards, and so only while the pointer is over this cell rather than a
    // neighbour in the same band. Going sideways is the board's job, not a cell's.
    axis: 'y',
    strength: cellAutoScrollRate,
    deadZoneRatioY: VERTICAL_DEAD_ZONE_RATIO,
  })

  useResetScroll(bodyRef, resetScrollKey)

  useScrollEdges(bodyRef, 'block')

  // Keyed on the two halves rather than on `address` itself: callers build the address
  // inline, so a fresh object arrives on every render and depending on it would defeat
  // the memo entirely.
  const cell = useMemo(
    () => cellsByAddress.get(composeCellKey({columnKey, rowKey})),
    [cellsByAddress, columnKey, rowKey],
  )

  useBoardItemApproach({address, cell, bodyRef, isDragging})

  const itemIds = useMemo<readonly ItemId[]>(
    () => (cell ? cell.items.map(item => getItemId(item)) : []),
    [cell, getItemId],
  )

  const column = columnsByKey.get(columnKey)
  const row = rowsByKey.get(rowKey)

  /*
    Every intersection is a drop target, and this is the whole of what makes that true.

    Registered unconditionally rather than for the cells that hold cards, which is what
    makes the set of targets independent of what has paged in: a cell the caller never
    supplied and a cell whose cards have not arrived are both places a card can be put,
    and a drop that failed because its destination had not loaded would be a drop that
    failed for a reason nobody could see.

    Nothing smaller registers. A card is not a target and neither is the space between
    two, so a pointer anywhere in the band resolves to exactly one candidate and there is
    nothing to disambiguate.

    Two things have to hold first. Both halves of the address have to resolve, the same
    guard the role and the name go through below: a key with no column behind it is not a
    cell on this board and must not answer a drop. And the board has to move cards at all,
    because a drop nobody is told about is not a destination; a board with no `onItemsMove`
    drags nothing either, so this withdraws the other half of a gesture that is already
    gone rather than half-disabling a live one.

    `disabled` is how both are withheld, and it does not mean unregistered -- dnd-kit
    registers a droppable's node keyed on its id alone and applies `disabled` from a later
    effect, which is what keeps the container out of collision detection and out of the
    measuring a drag does. That last part is why this is withheld at all on a board that
    cannot move cards: a caller dragging something of its own through its tree would
    otherwise have every drawn cell measured against it for a drop that could never be
    reported. The ref is withheld with the address guard, so a cell that is not on this
    board has no node to measure either.
  */
  const canMoveCards = useBoardCanMoveCards()
  const isCell = column !== undefined && row !== undefined
  const dropData = useMemo<BoardCellDropData>(
    () => ({type: BOARD_CELL_DROP_TYPE, columnKey, rowKey}),
    [columnKey, rowKey],
  )
  const {
    setNodeRef: setDropRef,
    isOver: isDragOver,
    over,
  } = useDroppable({
    id: boardCellDropId({columnKey, rowKey}),
    data: dropData,
    disabled: !isCell || !canMoveCards,
  })

  // Gated on the board carrying one of its own cards, because the library's answer is
  // about whatever is being dragged rather than about a card: a caller dragging anything
  // at all through its own tree would otherwise light every cell it crossed. The same fact
  // the auto-scroll above is switched on by, so a drag stays one question rather than two.
  const isOver = isDragOver && draggingCards.length > 0

  /*
    Where in this cell the held card would land, and the whole of what makes a place inside
    a cell something a reader can aim at.

    Only on a cell the caller hand-orders. Everywhere else the order is the board's sort,
    which would put the card back wherever it was dropped, so there is no position to offer
    and none is drawn. Asked of the caller per cell, because one board can hold both.

    The cell is still the only drop target and nothing here registers another. What is
    added is a reading of where the pointer is inside the cell that already answered, which
    is what keeps this safe for a windowed board: the cell is rendered whenever it is in the
    board's windows, so a drag always has something present to aim at, and the cards this
    measures against are exactly the ones a reader can see.
  */
  const canReorderCards = useBoardCellOrdering()
  // Called during render rather than memoised. A caller states this inline, so a memo
  // keyed on the function would be rebuilt every render anyway, and the answer is a
  // boolean the caller works out from an address it already has.
  const isHandOrdered = isCell && canMoveCards && canReorderCards(address)
  const isReordering = isOver && isHandOrdered

  // Which loaded card carries which id, so a drawn card's element resolves to its place
  // among every card the cell holds rather than to its place in the drawn run.
  const indexByItemId = useMemo(() => new Map(itemIds.map((id, index) => [id, index])), [itemIds])

  // The card in hand, when this is the cell it came from. Every index in any other cell is
  // a real destination, including the one the card would occupy there.
  const draggedCard = draggingCards[0]
  const sourceIndex =
    draggedCard !== undefined && draggedCard.columnKey === columnKey && draggedCard.rowKey === rowKey
      ? (indexByItemId.get(draggedCard.itemId) ?? null)
      : null
  const heldItemId = sourceIndex === null || draggedCard === undefined ? null : draggedCard.itemId

  const [dropIndex, setDropIndex] = useState<number | null>(null)

  /*
    Where the pointer last was, and the cards to measure it against, both held rather than
    closed over.

    A drag is exactly when a cell's cards change under it: reaching the end of one pages it,
    and this is the moment its own auto-scroll is carrying the reader there. Rebinding the
    listener on every such change would clear the answer each time and leave the reader
    with no line at all until they moved again, which on a cell that is still fetching is
    most of the drag. Held, the same point is measured against whatever the cell now draws.

    Following `drag/BoardDragProvider.tsx`, which holds the caller's own handler this way
    for the same reason.
  */
  const pointerYRef = useRef<number | null>(null)
  const indexByItemIdRef = useRef(indexByItemId)
  const heldItemIdRef = useRef(heldItemId)

  // Where the pointer already is, for the answer this cell gives on the way in. See
  // `BoardDragPointerYContext`.
  const dragPointerYRef = useBoardDragPointerYRef()

  useLayoutEffect(() => {
    indexByItemIdRef.current = indexByItemId
    heldItemIdRef.current = heldItemId
  }, [indexByItemId, heldItemId])

  const measureDropIndex = useCallback(() => {
    const body = bodyRef.current
    const pointerY = pointerYRef.current
    if (body === null || pointerY === null) return

    setDropIndex(insertionIndexAt(pointerY, drawnCardsIn(body, indexByItemIdRef.current, heldItemIdRef.current)))
  }, [bodyRef])

  /*
    One listener, bound only while this cell is the one under the pointer, so a drag costs
    the board a single subscription however many cells it has drawn.

    A pointer event rather than the drag library's own movement callback: subscribing to
    that means subscribing to a context that is rebuilt on every pointer move, and every
    drawn cell would re-render for a position all but one of them would discard. This
    re-renders one cell, and only when the answer changes, which is once per card crossed.

    The move that carried the drag in happened before any of this was bound, so the first
    answer is taken from the pointer the drag provider holds rather than from a move this
    cell has yet to hear. A reader who crosses into a cell and stops there has made that
    move and no other, and everything below -- the scroll, the page arriving -- measures
    the point they stopped at.

    Scroll is listened for in the capture phase, because a scroll event does not bubble and
    the container that moves may be this cell's body, the board around it, or anything the
    caller has put between them. Content moving under a pointer that is holding still is
    what auto-scroll is: without this the room would stay in the gap it opened in while
    the cards slid out from under it, and the drop would name a neighbour the reader had
    already scrolled away from. A capture listener on the view hears every scroll in the
    document, so what cannot have moved these cards is dropped before anything is measured:
    a page carrying a board is likely to be carrying other scrollers too, and each reading
    is a run of rectangles.
  */
  useEffect(() => {
    if (!isReordering) return

    const view = bodyRef.current?.ownerDocument.defaultView
    if (!view) return

    pointerYRef.current = dragPointerYRef.current
    measureDropIndex()

    const track = (event: PointerEvent) => {
      pointerYRef.current = event.clientY
      measureDropIndex()
    }

    const onScroll = (event: Event) => {
      const body = bodyRef.current
      // `contains` counts the node itself, so this is the cell's own body and everything
      // it hangs from, the document included.
      if (body !== null && event.target instanceof Node && event.target.contains(body)) measureDropIndex()
    }

    view.addEventListener('pointermove', track)
    // eslint-disable-next-line github/prefer-observers -- no observer reports where a live card rect has moved to
    view.addEventListener('scroll', onScroll, {capture: true})

    return () => {
      view.removeEventListener('pointermove', track)
      view.removeEventListener('scroll', onScroll, {capture: true})
      pointerYRef.current = null
      setDropIndex(null)
    }
  }, [isReordering, bodyRef, measureDropIndex, dragPointerYRef])

  // Every redraw of the cell changes what the point the pointer is resting on means, and a
  // reader who has stopped moving is exactly who one arrives for: a page fills the cell
  // beneath them, and the window draws the cards a scroll has just brought into view.
  // Measured again rather than left naming a place among the cards that used to be there.
  // No dependency list, so this runs after every commit of the cell. React compares the
  // result and drops a render that would change nothing, so a measurement that agrees with
  // the one before it costs a read and no more.
  useEffect(() => {
    if (isReordering) measureDropIndex()
  })

  /*
    Where in the cell's loaded cards the reader is aiming, once the drawn run has been
    resolved back to the cards the cell holds.

    A cell that draws no card answers `0` rather than nothing, because there is only one
    place in it and the reader is aiming at it. That is not the same as having drawn none
    yet: a cell holding cards keeps its own counsel until a pointer move has said where
    inside it the reader is.
  */
  const insertionIndex = !isReordering ? null : (dropIndex ?? (itemIds.length === 0 ? 0 : null))

  const placement = useMemo<BoardDropPlacement | null>(() => {
    if (insertionIndex === null) return null
    if (sourceIndex !== null && isRedundantInsertion(sourceIndex, insertionIndex)) return null

    const anchor = cellDropAnchor(itemIds, insertionIndex, {hasMoreItems: cellHasUnloadedItems(cell)})

    return anchor === undefined ? {columnKey, rowKey} : {columnKey, rowKey, anchor}
  }, [insertionIndex, sourceIndex, itemIds, cell, columnKey, rowKey])

  /*
    The same fact, handed to the one place that has to report it. Exactly one cell writes,
    because exactly one cell is under the pointer, and this clears on the way out so a drop
    is never answered by a cell the reader has dragged out of.

    React runs every cleanup in a commit before any of the layout effects, so a drag crossing
    from one cell into another clears the first before the second writes, whichever order the
    two are drawn in. Publishing in the same commit as the insertion marker also lets an
    immediate pointer release report the placement that marker shows.
  */
  const dropPlacementRef = useBoardDropPlacementRef()

  useLayoutEffect(() => {
    if (placement === null) return

    dropPlacementRef.current = placement

    return () => {
      dropPlacementRef.current = null
    }
  }, [placement, dropPlacementRef])

  // The cell the held card is headed for. Kept while the pointer crosses a gutter or a
  // header, so the cell the card came from does not reopen its slot at every gap between
  // two cells.
  const overCell = readBoardCellDropData(over)
  const overCellKey = overCell === null ? null : composeCellKey(overCell)
  const [headedForKey, setHeadedForKey] = useState<string | null>(null)
  const nextHeadedForKey = sourceIndex === null ? null : (overCellKey ?? headedForKey)
  if (nextHeadedForKey !== headedForKey) setHeadedForKey(nextHeadedForKey)
  const isHeldCardLeaving = nextHeadedForKey !== null && nextHeadedForKey !== composeCellKey({columnKey, rowKey})

  const isDraggingCard = draggedCard !== undefined
  const settlingMove = useBoardSettlingMove()
  const cardRoom = useMemo<BoardCardRoom | null>(() => {
    if (isDraggingCard) return draggingCardRoom({heldIndex: sourceIndex, dropIndex: insertionIndex, isHeldCardLeaving})
    if (settlingMove === null) return null

    return settlingCardRoom({columnKey, rowKey}, itemIds, settlingMove, {isRanked: isHandOrdered})
  }, [
    isDraggingCard,
    sourceIndex,
    insertionIndex,
    isHeldCardLeaving,
    settlingMove,
    columnKey,
    rowKey,
    itemIds,
    isHandOrdered,
  ])

  // The cell itself as a focus position: this column, this row, and no card. Keyed on the
  // two halves for the same reason the cell lookup above is.
  const position = useMemo<BoardFocusPosition>(() => cellPosition({columnKey, rowKey}, null), [columnKey, rowKey])
  const {isRovingItem, onFocus} = useRovingFocus(position, bodyRef)

  const focusedCardIndex = useFocusedCardIndex(address, itemIds)
  const isCardFocusPending = useIsCardFocusPending(address)

  const totalCount = cell?.totalCount
  const hasUnloadedItems = cellHasUnloadedItems(cell)
  const setSize = cardSetSize({totalCount, hasMoreItems: hasUnloadedItems, loaded: itemIds.length})

  return {
    itemIds,
    totalCount,
    heading: isCell ? column.label : undefined,
    headingLevel: cellHeadingLevel,
    cardSetSize: setSize,
    hasMoreItems: hasUnloadedItems,
    isLoadingItems: cell?.isLoadingItems ?? false,
    // Both halves have to resolve before this is a cell at all: a key with no column
    // or no row behind it is not a real intersection, so it registers no drop node, for
    // the reason recorded above the registration.
    cellProps: isCell ? {ref: setDropRef} : {},
    isOver,
    canReorderCards: isHandOrdered,
    dropIndex: insertionIndex,
    cardRoom,
    // The cell's group, on the element focus lands on, so a reader arriving on a cell
    // hears its column and swimlane once rather than an unnamed scroll container inside
    // a separately named group. It gets no role unless both halves of the address
    // resolve, because an unnamed group is dead semantics and naming a cell after only
    // the half that resolved would let an address that does not exist on this board
    // answer the test-utils locators.
    //
    // The `tabIndex` is the board's roving one rather than a stop of the cell's own, and
    // it is absent entirely until the position is on this cell. A card is left reachable
    // by pointer at `-1`, because clicking one is how a reader moves the board's position
    // to it. A cell body is not: it fills the whole band, so the blank space under the
    // last card is this element, and a `-1` here would let a click there take the roving
    // item off a card and onto an unnamed scroll container.
    bodyProps: {
      ref: setRef,
      ...(isCell
        ? {
            role: 'group',
            'aria-label': labels.cell({columnLabel: column.label, rowLabel: hasRowHeaders ? row.label : undefined}),
          }
        : {}),
      tabIndex: isRovingItem ? 0 : undefined,
      onFocus,
    },
    bodyRef,
    focusedItemIndex: focusedCardIndex === -1 ? null : focusedCardIndex,
    isCardFocusPending,
    isEmpty: itemIds.length === 0,
  }
}

/**
 * The cards this cell has in the page, each with its place among the cards the cell holds
 * and its edges on screen.
 *
 * In document order, which is the order the cards are in, so nothing here has to sort. A
 * card the cell no longer holds is skipped rather than guessed at: it is one React has
 * drawn from data that has since been replaced, and it has no place in the new order to
 * report. So is the held card, named by `skipItemId`, in the cell it came from.
 */
function drawnCardsIn(
  body: HTMLElement,
  indexByItemId: ReadonlyMap<ItemId, number>,
  skipItemId: ItemId | null,
): DrawnCard[] {
  const cards: DrawnCard[] = []

  for (const element of body.querySelectorAll(`[${BOARD_CARD_ID_ATTRIBUTE}]`)) {
    const itemId = element.getAttribute(BOARD_CARD_ID_ATTRIBUTE)
    const index = itemId === null || itemId === skipItemId ? undefined : indexByItemId.get(itemId)
    if (index === undefined) continue

    /* eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-loop -- each card is
       a separate box and no single read gives all of them. Bounded by the cards the cell has
       drawn, which its window keeps to what fits on screen, and read only while a card is
       being dragged over this one cell. */
    const {top, bottom} = element.getBoundingClientRect()
    cards.push({index, start: top, end: bottom})
  }

  return cards
}
